import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import request from 'utils/request';
import urls from 'utils/config';
import orgConfig from 'utils/orgConfig';
import { selectCanonicalOrgId } from 'redux/configurations/selectors';

const Wrap = styled.div`
  position: relative;
`;

const Trigger = styled.button`
  width: 35px;
  height: 35px;
  border-radius: 100px;
  border: 1px solid #e4e7ec;
  background: #fff;
  color: #344054;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const Menu = styled.div`
  position: absolute;
  top: 42px;
  right: 0;
  width: 220px;
  padding: 6px;
  background: #fff;
  border: 1px solid #e4e7ec;
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(16, 24, 40, 0.16);
  z-index: 30;
`;

const Label = styled.div`
  padding: 6px 8px 4px;
  font-size: 11px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #667085;
`;

const Item = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  text-align: left;
  cursor: pointer;
  &:hover:not(:disabled) {
    background: #f9fafb;
  }
`;

const Mark = styled.span`
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: #f2f4f7;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 600;
`;

function GridIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden>
      {[2, 7, 12].map((y) =>
        [2, 7, 12].map((x) => (
          <rect key={`${x}-${y}`} x={x} y={y} width="2.2" height="2.2" rx="0.4" fill="currentColor" />
        ))
      )}
    </svg>
  );
}

/** Other apps this person already has at this school. Hidden when there are none. */
const AppSwitcher = () => {
  const orgId = useSelector(selectCanonicalOrgId);
  const [apps, setApps] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!orgId) return undefined;
    let cancelled = false;
    const url = `${urls.UMS_BASE_URL}/auth/products/${orgConfig.product}/organisations/${orgId}/apps`;
    request({ url }).then((response) => {
      if (cancelled) return;
      setApps(response.success && Array.isArray(response.raw) ? response.raw : []);
    });
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => {
      if (wrapRef.current && wrapRef.current.contains(event.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  if (!apps || apps.length === 0) return null;

  const choose = (app) => {
    setBusy(app.id);
    setError(null);
    const url = `${urls.UMS_BASE_URL}/auth/products/${orgConfig.product}/organisations/${orgId}/apps/${encodeURIComponent(app.id)}/open`;
    request({ method: 'post', url, data: {} }).then((response) => {
      if (response.success && response.raw && response.raw.url) {
        window.location.assign(response.raw.url);
        return;
      }
      setBusy(null);
      setError(`Could not open ${app.name}`);
    });
  };

  return (
    <Wrap ref={wrapRef}>
      <Trigger type="button" aria-label="Your apps" title="Your apps" onClick={() => setOpen((value) => !value)}>
        <GridIcon />
      </Trigger>
      {open ? (
        <Menu role="menu" aria-label="Your apps">
          <Label>Your apps</Label>
          {apps.map((app) => (
            <Item key={app.id} type="button" role="menuitem" disabled={busy !== null} onClick={() => choose(app)}>
              <Mark>{(app.name || '?').slice(0, 1)}</Mark>
              <span>{busy === app.id ? 'Opening…' : app.name}</span>
            </Item>
          ))}
          {error ? <Label>{error}</Label> : null}
        </Menu>
      ) : null}
    </Wrap>
  );
};

export default AppSwitcher;
